/**
 * **D7-bundle (FI-17 follow-up)** — Helper SSOT per il flow di acquisizione
 * bundle all-inclusive (manuale V1 rr. 1764-1773).
 *
 * Un bundle è l'insieme delle mosse aggiuntive aggregate automaticamente
 * con la presa di una mossa **anchor** che le dichiara via
 * `MoveSchema.bundles`. Esempio manuale r. 1770:
 *
 * > «Grug il Guerriero opta per *Lanciare Incantesimi Arcani*, ottenendo
 * > in omaggio *Corpus Arcano* e *Mana*.»
 *
 * **Anchor del bundle** (decisione D7-bundle-Q1, 2026-05-14): la mossa di
 * casting è sempre l'anchor. Es.: `mago.lanciare-incantesimi-arcani.bundles
 * = ['mago.corpus-arcano', 'mago.mana']`. Coerente con manuale r. 1770.
 *
 * **Dedup Mana cross-class** (decisione D7-bundle-Q2, 2026-05-14): se il
 * PG possiede già una mossa con tag `'mana'` (es. `mago.mana` perché è un
 * Mago L1), il bundle skippa silenziosamente le altre `*.mana`. Manuale
 * r. 1133: «*Mana* … costituiscono una **riserva unica**».
 *
 * **Cosa contiene questo modulo**:
 *  - {@link isManaMove}: predicate data-driven `tags.includes('mana')`.
 *  - {@link alreadyOwnsManaMove}: il PG ha già una qualsiasi mossa Mana?
 *  - {@link getBundleMovesToApply}: risolve `move.bundles` in `Move[]`
 *    applicando filtri idempotenza + dedup Mana.
 *
 * **Cosa NON contiene**: l'orchestrazione `applyBundle` vive direttamente
 * dentro `applyMoveAcquisition` (con ricorsione 1-livello bloccata da
 * `source !== 'bundle'`) per evitare import circolari.
 */

import { findMoveByIdGlobal } from '../data/moves';
import type { Character, MoveRef } from '../schemas/character';
import type { Move } from '../schemas/move';

/**
 * **FI-38 Opz. C (2026-05-14)** — Anchor moves le cui mosse del bundle NON
 * sono tipicamente in `startingMoves` automatici / `startingChoiceGroup`
 * della classe, perché vengono acquisite **come bundle** quando il giocatore
 * seleziona l'anchor.
 *
 * Esempi:
 *  - `warlock.vincolo-del-tomo` → bundle `[warlock.mana, warlock.lanciare-incantesimi-del-patrono]`
 *  - `druido.divinita-della-natura` → bundle cross-class `[chierico.lanciare-incantesimi-divini, chierico.mana]`
 *  - `paladino.ordine-sacro` → bundle cross-class idem Druido
 *
 * **Differenza con Mago/Chierico**: per Mago/Chierico le 3 mosse del trio
 * (`corpus-arcano` / `divinita` + `mana` + `lanciare-incantesimi-X`) sono
 * AUTO in `startingMoves` della classe, quindi NON serve espandere il bundle
 * — sono già in `draft.moves` durante editing builder L1.
 *
 * Usato da:
 *  - `finalizeCharacter` (al finalize per chiamare `applyMoveAcquisition`
 *    con `expandBundle: true` per le sole anchor che lo richiedono);
 *  - {@link expandStartingMoveRefsWithBundles} (per propagare i bundle
 *    members nel draft del builder, in modo che `getActiveSpellLists` veda
 *    le mosse di lancio cross-class).
 */
export const ANCHORS_REQUIRING_BUNDLE_EXPANSION: ReadonlySet<string> = new Set([
  'warlock.vincolo-del-tomo',
  'druido.divinita-della-natura',
  'paladino.ordine-sacro',
]);

/**
 * Una mossa è "Mana" se ha il tag `'mana'` (data-driven).
 *
 * Il tag è applicato a `chierico.mana` + `mago.mana` (e in futuro
 * `warlock.mana` tramite FI-38) per abilitare il dedup cross-class senza
 * hardcodare un set di ID nel service.
 */
export function isManaMove(move: Move): boolean {
  return move.tags.includes('mana');
}

/**
 * Il PG possiede già una qualsiasi mossa con tag `'mana'`? Lookup su
 * `character.moves` via {@link findMoveByIdGlobal}.
 *
 * Usato dal filtro bundle per implementare la regola «*Mana* è una
 * riserva unica» (manuale V1 r. 1133): se il PG è già un Mago L1 (ha
 * `mago.mana`) e via multiclasse pesca `chierico.lanciare-incantesimi-divini`
 * (bundle `[chierico.divinita, chierico.mana]`), `chierico.mana` viene
 * skippato — il PG conserva la sua singola riserva di Mana.
 */
export function alreadyOwnsManaMove(character: Character): boolean {
  for (const moveRef of character.moves) {
    const move = findMoveByIdGlobal(moveRef.id);
    if (move && isManaMove(move)) return true;
  }
  return false;
}

/**
 * Risolve gli ID di {@link Move.bundles} in `Move[]`, applicando filtri
 * nell'ordine:
 *
 *  1. Lookup `findMoveByIdGlobal` — throws se l'ID non è nel catalogo
 *     (errore di data-entry, segnalato eagerly invece di silently skip).
 *  2. **Idempotenza**: mossa già posseduta dal PG → skip silenzioso.
 *  3. **Dedup Mana cross-class**: se la mossa ha tag `'mana'` e il PG
 *     possiede già una qualsiasi mossa Mana → skip silenzioso.
 *
 * Output: array delle sole mosse del bundle che vanno effettivamente
 * acquisite. Vuoto se la mossa anchor non ha `bundles` o se tutto è
 * filtrato (caso comune: builder L1 dove l'anchor + bundle members
 * sono già tutti in `Character.moves` perché iterati dal builder
 * separatamente).
 *
 * @throws Error se un ID nel bundle non esiste nel catalogo globale.
 */
/**
 * **FI-60** — Espande i `MoveRef` di un draft includendo le mosse implicite
 * che il giocatore acquisisce automaticamente con la presa di una anchor.
 *
 * Pensato per disaccoppiare l'editing builder L1 dal `finalizeCharacter`:
 * durante l'editing `draft.moves` contiene solo le mosse pickate dal
 * giocatore (anchor: `paladino.ordine-sacro`, `paladino.ordine-sovrannaturale`),
 * ma `getActiveSpellLists` (e altri consumer cross-class) si aspetta di vedere
 * anche le mosse implicite (`chierico.lanciare-incantesimi-divini`,
 * `warlock.vincolo-del-tomo`, ecc.). Questo helper proietta le 2 fonti di
 * mosse implicite come `MoveRef` stub `{ id, selectedChoices: [] }` in modo
 * idempotente.
 *
 * **Due fonti di mosse implicite**:
 *
 *  1. **Bundle diretti** (`Move.bundles`): per le anchor in
 *     {@link ANCHORS_REQUIRING_BUNDLE_EXPANSION} (Paladino-Sacro,
 *     Druido-Natura, Warlock-Tomo) i bundle members non sono in `startingMoves`
 *     della classe → vanno acquisiti col bundle.
 *
 *  2. **Granted choices cross-class** (`Move.grantsMoveChoice` con
 *     `kind: 'from-choice-mapping'`): per le anchor che concedono una mossa
 *     cross-class in base alla `selectedChoices[0]` del giocatore (Paladino-
 *     Sovrannaturale → Vincolo Warlock). La granted move può a sua volta essere
 *     anchor di un bundle (es. `warlock.vincolo-del-tomo`) → l'expansion è
 *     iterata 2 passi (max 3 per safety) per coprire la catena.
 *
 * **Idempotenza**: se una mossa implicita è già nei `moves` (caso
 * post-finalize `Character.moves`), viene mantenuta invariata (preservando
 * eventuali `selectedChoices` / `selectedChoiceGroups` compilati). Solo le
 * mosse **mancanti** vengono aggiunte come stub.
 *
 * **Niente effetti collaterali** (a differenza di `applyMoveAcquisition`):
 * non applica spell-grants, non chiama `replaces`, non valuta dedup Mana
 * cross-class — è una pura proiezione strutturale per l'editing.
 *
 * @returns array con tutti i `moves` originali + le mosse implicite mancanti
 *          aggiunte in coda. Stesso riferimento di input se nessuna addizione.
 */
export function expandStartingMoveRefsWithBundles(moves: readonly MoveRef[]): readonly MoveRef[] {
  let current = moves;
  // Max 3 passi: copre la catena anchor → granted (anchor di bundle) → bundle members.
  // In pratica per il catalogo DWR attuale bastano 2 passi (Paladino-Sovrannaturale-Tomo).
  for (let pass = 0; pass < 3; pass++) {
    const next = expandOnce(current);
    if (next === current) return current;
    current = next;
  }
  return current;
}

function expandOnce(moves: readonly MoveRef[]): readonly MoveRef[] {
  const ownedIds = new Set(moves.map((m) => m.id));
  const additions: MoveRef[] = [];

  function addIfMissing(id: string): void {
    if (ownedIds.has(id)) return;
    ownedIds.add(id);
    additions.push({ id, selectedChoices: [] });
  }

  for (const ref of moves) {
    const move = findMoveByIdGlobal(ref.id);
    if (!move) continue;
    // Path A: bundle diretto (Paladino-Sacro / Druido-Natura / Warlock-Tomo)
    if (ANCHORS_REQUIRING_BUNDLE_EXPANSION.has(ref.id) && move.bundles) {
      for (const bundleId of move.bundles) addIfMissing(bundleId);
    }
    // Path B: granted choice cross-class (Paladino-Sovrannaturale → Vincolo Warlock)
    if (move.grantsMoveChoice?.kind === 'from-choice-mapping') {
      const selectedId = ref.selectedChoices[0];
      if (selectedId !== undefined) {
        const grantedId = move.grantsMoveChoice.mapping[selectedId];
        if (grantedId !== undefined) addIfMissing(grantedId);
      }
    }
  }
  return additions.length === 0 ? moves : [...moves, ...additions];
}

/**
 * **FI-46bis** — Inverso di {@link expandStartingMoveRefsWithBundles}.
 *
 * Rimuove dai `moves` le mosse che sono "membri di bundle" di un'**anchor
 * speciale** ({@link ANCHORS_REQUIRING_BUNDLE_EXPANSION}) presente nello
 * stesso array. Usato da `characterToDraft` (edit retroattivo L1) per
 * comprimere `Character.moves` (post-finalize, espanso) in una shape
 * `CharacterDraft.moves` compatibile con il re-finalize del builder edit.
 *
 * **Why**: `finalizeCharacter` per le 3 anchor speciali (Warlock-Tomo,
 * Druido-DivNat, Paladino-OrdineSacro) imposta `expandBundle: true` →
 * applyMoveAcquisition aggiunge automaticamente i bundle members. Se in
 * input passiamo SIA l'anchor SIA i membri, l'iterazione duplica i membri.
 *
 * **Idempotente**: se nessuna anchor speciale è presente, ritorna `moves`
 * invariato (stessa reference). Le mosse non-bundle vengono preservate
 * con il loro setup originale.
 *
 * @returns array senza i membri di bundle delle anchor speciali presenti.
 *          Le anchor sono preservate; le mosse non-bundle sono preservate.
 */
export function stripExpandedBundleMembers(moves: readonly MoveRef[]): readonly MoveRef[] {
  const memberIdsToStrip = new Set<string>();
  for (const ref of moves) {
    if (!ANCHORS_REQUIRING_BUNDLE_EXPANSION.has(ref.id)) continue;
    const anchorMove = findMoveByIdGlobal(ref.id);
    if (!anchorMove?.bundles) continue;
    for (const memberId of anchorMove.bundles) {
      memberIdsToStrip.add(memberId);
    }
  }
  if (memberIdsToStrip.size === 0) return moves;
  return moves.filter((m) => !memberIdsToStrip.has(m.id));
}

export function getBundleMovesToApply(anchorMove: Move, character: Character): readonly Move[] {
  if (!anchorMove.bundles || anchorMove.bundles.length === 0) return [];

  const ownedIds = new Set(character.moves.map((m) => m.id));
  const ownsMana = alreadyOwnsManaMove(character);

  const result: Move[] = [];
  for (const bundleId of anchorMove.bundles) {
    const bundleMove = findMoveByIdGlobal(bundleId);
    if (!bundleMove) {
      throw new Error(
        `Mossa "${bundleId}" del bundle di "${anchorMove.id}" non trovata nel catalogo globale.`,
      );
    }
    if (ownedIds.has(bundleId)) continue;
    if (isManaMove(bundleMove) && ownsMana) continue;
    result.push(bundleMove);
  }
  return result;
}
